/**
 * Spanish catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 1.
 *
 * `dir: 'ltr'`. Native-grade Spanish at a mathematician's register. **Warrant → «Garantía»**
 * (the established Spanish rendering of Toulmin's warrant). The empirical kinds follow
 * Popperian usage in Spanish — testable/tested as «contrastable» / «contrastado».
 *
 * W/M grades stay Latin (`::numbering-system/latn`) — a grade is a stable id, not prose.
 * CLDR cardinal categories for Spanish are one / many / other (`many` covers the large
 * compact-number form), so `frameActive` declares exactly those (plus an explicit `=0`).
 * Per INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const ES_CATALOG = {
    locale: 'es',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Indagación',
        [P2.sectionClaims]: 'Afirmaciones',
        [P2.sectionIntegrity]: 'Integridad',
        [P2.axisWarrantLabel]: 'Garantía',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empírico',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definición} interp {Interpretación} testable {Contrastable} tested {Contrastado} refuted {Refutado} program {Programa de investigación} philos {Filosófico} other {Desconocido}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Nivel de fuerza {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Explorable',
        [P2.stateRefuted]: 'Refutado',
        [P2.stateOutside]: 'Fuera del alcance',
        [P2.verdictConflict]: 'En conflicto con {constraints}',
        [P2.verdictOutside]: 'Necesita {missing}',
        [P2.lossRegisterPlain]: 'Simplificado a lenguaje sencillo — se omite parte de la precisión.',
        [P2.lossRegisterKid]: 'Simplificado para lectores jóvenes — se omiten muchos detalles.',
        [P2.lossProfileDetail]: 'El perfil completo de Garantía / Empírico / Modal se resume aquí.',
        [P2.lossConflictDetail]: 'Los detalles del conflicto se abrevian en esta vista.',
        [P2.laneVerified]: 'Verificado',
        [P2.laneUnverified]: 'No verificado',
        [P2.laneConflict]: 'Conflicto probado',
        [P2.constraintLogic]: 'Lógica',
        [P2.constraintLightBends]: 'La luz se curva en un campo gravitatorio',
        [P2.constraintParallelPostulate]: 'El postulado de las paralelas',
        [P2.claimScalarGravity]: 'Teoría escalar de la gravedad',
        [P2.claimNonEuclidean]: 'Geometría no euclidiana',
        [P2.claimSubstrateSr]: 'Interpretación por sustrato de la relatividad especial',
        [P2.edgeExtObservation]: 'Observación externa',
        [P2.edgeResourceSample]: 'Recurso computacional compartido',
        [P2.edgeHeuristicSample]: 'Orientación heurística',
        [P2.edgeSharedSample]: 'Fundamento compartido',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Restricciones',
        [P2.sectionEdges]: 'Aristas',
        [P2.sectionFrame]: 'Marco',
        [P2.sectionExposure]: 'Exposición',
        [P2.statusAdoptedFormal]: 'Adoptado (formal)',
        [P2.statusAdoptedInterpretive]: 'Adoptado (interpretativo)',
        [P2.statusSuspended]: 'Suspendido',
        [P2.edgeEffectConstraint]: 'Compone una restricción (puede refutar)',
        [P2.edgeEffectResource]: 'Recurso — sin efecto en los veredictos',
        [P2.edgeEffectHeuristic]: 'Heurística — sin efecto en los veredictos',
        [P2.edgeEffectShared]: 'Fundamento compartido — sin efecto en los veredictos',
        [P2.frameActive]: '{count, plural, =0 {Sin restricciones activas} one {# restricción activa} many {# restricciones activas} other {# restricciones activas}}',
        [P2.exposureNone]: 'Sin dependencias interpretativas',
        [P2.exposureDep]: 'Depende de {id}',
        [P2.laneTrackMachine]: 'Máquina',
        [P2.laneTrackExpert]: 'Experto',
        [P2.laneTrackCommunity]: 'Comunidad',
        [P2.expertAudited]: 'Auditado',
        [P2.expertInProgress]: 'En curso',
        [P2.expertNotReviewed]: 'No revisado',
        [P2.communityFlags]: 'Hay reportes',
        [P2.communityNone]: 'Sin reportes',
        [P2.controlChallenge]: 'Cuestionar',
        [P2.controlActivate]: 'Activar',
        [P2.controlMakeInterpretive]: 'Hacer interpretativo',
        [P2.controlImportEdge]: 'Importar arista',
        [P2.controlPromote]: 'Promover',
        [P2.controlUndo]: 'Deshacer',
        [P2.controlRedo]: 'Rehacer',
        [P2.controlRegister]: 'Registrar',
        [P2.promoteRejected]: 'Promoción rechazada',
        [P2.promoteAccepted]: 'Promoción aceptada',
        [P2.promoteReasonRefutationTerminal]: 'Una prueba no puede subsanar una refutación.',
        [P2.promoteReasonKeystone]: 'Una promoción clave requiere apoyo empírico.',
        [P2.promoteReasonEmpiricalNotFixed]: 'El tipo empírico no está fijado.',
        [P2.promoteReasonModalNotFixed]: 'El grado modal no está fijado.',
        [P2.promoteReasonWarrantNotImproved]: 'El grado de garantía no ha mejorado.',
        [P2.evidenceComputation]: "{engine} calculó {op} = {result} bajo {config}",
        [P2.lossNumericRounding]: "Resultado redondeado a {dp} decimales",
    },
};
//# sourceMappingURL=es.js.map